/**
 * Integrations: API keys for a restaurant's own software, and webhooks that
 * tell it what happened.
 *
 * Two things here are shown once and never again — a new API key and a
 * webhook's signing secret. The server keeps a hash of the one and an
 * encrypted copy of the other, and no call returns either after the response
 * that made it. So the screens hold them in component state for as long as
 * the sheet is open, and nowhere else.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type ApiKeyStatus = 'active' | 'expired' | 'revoked';

export interface ApiKey {
  id: string;
  name: string;
  /** Enough of the key to tell which one it is: `rk_1a2b3c4d…`. */
  prefix: string;
  /** Permission strings, the same ones a role carries. */
  scopes: string[];
  /** The one site it is confined to. Null: every site. */
  location_id: string | null;
  expires_at: string | null;
  created_at: string;
  created_by: string;
  created_by_name: string | null;
  revoked_at: string | null;
  last_used_at: string | null;
  status: ApiKeyStatus;
}

export interface NewApiKey {
  key: ApiKey;
  /** The whole key. This is the only time it exists outside the program it is given to. */
  secret: string;
}

export interface ApiKeyInput {
  name: string;
  scopes: string[];
  locationId: string | null;
  /** ISO. Null: never. */
  expiresAt: string | null;
}

export interface WebhookEventType {
  /** Versioned: `order.closed.v1`. */
  type: string;
  label: string;
  description: string;
  fields: string[];
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string;
  events: string[];
  is_active: boolean;
  /** Why the system turned it off. Null when it is on, or a person turned it off. */
  disabled_reason: string | null;
  consecutive_failures: number;
  created_at: string;
}

export interface NewWebhookSecret {
  endpoint: WebhookEndpoint;
  /** `whsec_…`. Shown once. */
  secret: string;
}

export type DeliveryStatus = 'pending' | 'delivered' | 'failed';

export interface WebhookDelivery {
  id: string;
  endpoint_id: string;
  event_type: string;
  status: DeliveryStatus;
  attempts: number;
  /** The receiver's HTTP status. Null when there was no answer at all. */
  response_status: number | null;
  last_error: string | null;
  is_test: boolean;
  created_at: string;
  last_attempt_at: string | null;
  delivered_at: string | null;
  /** When it will be tried again. Null unless still waiting. */
  next_attempt_at: string | null;
}

export interface WebhookInput {
  url: string;
  events: string[];
  description: string;
}

const KEYS = ['integrations', 'api-keys'] as const;
const HOOKS = ['integrations', 'webhooks'] as const;

// --------------------------------------------------------------------------- //
// API keys
// --------------------------------------------------------------------------- //

export function useApiKeys(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: KEYS,
    queryFn: ({ signal }) => api.get<ApiKey[]>('/v1/integrations/api-keys', signal),
    enabled,
  });
}

/** What the signed-in person may put on a key: what they can do themselves. */
export function useApiKeyScopes(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['integrations', 'scopes'],
    queryFn: ({ signal }) => api.get<string[]>('/v1/integrations/scopes', signal),
    enabled,
  });
}

/** 402 below Professional; 409 with `field` for a scope, site or date that will not do. */
export function useCreateApiKey() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<NewApiKey, ApiError, ApiKeyInput>({
    mutationFn: (input) =>
      api.post<NewApiKey>('/v1/integrations/api-keys', {
        name: input.name,
        scopes: input.scopes,
        location_id: input.locationId,
        expires_at: input.expiresAt,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS });
    },
  });
}

/** Stops the key from its next request. On any plan. */
export function useRevokeApiKey() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ApiKey, ApiError, { keyId: string }>({
    mutationFn: ({ keyId }) => api.post<ApiKey>(`/v1/integrations/api-keys/${keyId}/revoke`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS });
    },
  });
}

// --------------------------------------------------------------------------- //
// Webhooks
// --------------------------------------------------------------------------- //

export function useWebhookEventTypes(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['integrations', 'event-types'],
    queryFn: ({ signal }) =>
      api.get<WebhookEventType[]>('/v1/integrations/webhooks/events', signal),
    enabled,
    staleTime: Infinity,
  });
}

export function useWebhooks(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: HOOKS,
    queryFn: ({ signal }) => api.get<WebhookEndpoint[]>('/v1/integrations/webhooks', signal),
    enabled,
  });
}

/** The endpoint's last fifty deliveries, newest first. */
export function useWebhookDeliveries(endpointId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: [...HOOKS, endpointId ?? null, 'deliveries'],
    queryFn: ({ signal }) =>
      api.get<WebhookDelivery[]>(`/v1/integrations/webhooks/${endpointId}/deliveries`, signal),
    enabled: Boolean(endpointId),
    // Retries happen on the server's clock; a log left open should show them.
    refetchInterval: 15_000,
  });
}

function useHookMutation<TInput, TResult>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TResult>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: HOOKS });
    },
  });
}

/** 402 below Professional or past the plan's number of endpoints; 409 with `field` for an address or event that will not do. */
export function useCreateWebhook() {
  return useHookMutation<WebhookInput, NewWebhookSecret>((api, input) =>
    api.post<NewWebhookSecret>('/v1/integrations/webhooks', input),
  );
}

/** Change the address or events, or turn it on or off. Turning off works on any plan. */
export function useUpdateWebhook() {
  return useHookMutation<
    { endpointId: string } & Partial<WebhookInput> & { isActive?: boolean },
    WebhookEndpoint
  >((api, { endpointId, isActive, ...change }) =>
    api.request<WebhookEndpoint>(`/v1/integrations/webhooks/${endpointId}`, {
      method: 'PATCH',
      body: { ...change, ...(isActive === undefined ? {} : { is_active: isActive }) },
    }),
  );
}

/** A new signing secret, shown once. The old one stops working at once. */
export function useReplaceWebhookSecret() {
  return useHookMutation<{ endpointId: string }, NewWebhookSecret>((api, { endpointId }) =>
    api.post<NewWebhookSecret>(`/v1/integrations/webhooks/${endpointId}/secret`),
  );
}

export function useRemoveWebhook() {
  return useHookMutation<{ endpointId: string }, void>((api, { endpointId }) =>
    api.request<void>(`/v1/integrations/webhooks/${endpointId}`, { method: 'DELETE' }),
  );
}

/** Sends a made-up event now; resolves with how it went. */
export function useSendTestWebhook() {
  return useHookMutation<{ endpointId: string }, WebhookDelivery>((api, { endpointId }) =>
    api.post<WebhookDelivery>(`/v1/integrations/webhooks/${endpointId}/test`),
  );
}
